import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar,
  Cell,
  PieChart,
  Pie,
  Legend,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from 'recharts'
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  FileText,
  Shield,
  TrendingUp,
  Target,
  ChevronDown,
  ChevronUp,
  Calendar,
  Users,
  FileCheck,
  Layers,
  GitCompare,
  Activity,
  ChevronRight,
} from 'lucide-react'

// ============================================================================
// TYPES
// ============================================================================

interface FrameworkDashboardData {
  framework: {
    id: string
    name: string
    display_name: string
    version: string | null
    description: string | null
    enabled: number
  }
  compliance_score: number
  controls: {
    total: number
    applicable: number
    compliant: number
    non_compliant: number
    in_progress: number
    pending: number
    not_applicable: number
    overdue: number
  }
  categories: Array<{
    name: string
    total: number
    compliant: number
    non_compliant: number
    in_progress: number
    pending: number
    not_applicable: number
    overdue: number
    compliance_percentage: number
  }>
  evidence: {
    total: number
    controls_with_evidence: number
    coverage_percentage: number
    last_30_days: number
    last_7_days: number
  }
  documents: {
    controls_with_policies: number
    total_policies: number
    approved_policies: number
  }
  overdue_controls: Array<{
    id: string
    control_number: string
    name: string
    days_overdue: number
    next_due_date: number
  }>
  upcoming_due: Array<{
    id: string
    control_number: string
    name: string
    status: string
    days_until_due: number
    next_due_date: number
  }>
}

interface TrendsData {
  framework_id: string
  framework_name: string
  period_days: number
  evidence_collection: Array<{ date: string; evidence_count: number }>
  compliance_timeline: Array<{ date: string; compliance_percentage: number; compliant: number }>
  current_stats: {
    total_controls: number
    applicable_controls: number
    compliant_controls: number
    compliance_percentage: number
  }
}

interface GapsData {
  framework_id: string
  framework_name: string
  summary: {
    total_controls: number
    applicable_controls: number
    compliant_controls: number
    gap_count: number
    gap_percentage: number
    not_started_count: number
    in_progress_count: number
    non_compliant_count: number
    unassigned_count: number
    missing_policies_count: number
  }
  not_started: Array<{
    id: string
    control_number: string
    name: string
    category: string
    frequency: string
    owner: string
    evidence_type: string
  }>
  in_progress: Array<{
    id: string
    control_number: string
    name: string
    category: string
    frequency: string
    owner: string
    days_until_due: number | null
    next_due_date: number | null
  }>
  non_compliant: Array<{
    id: string
    control_number: string
    name: string
    category: string
    frequency: string
    owner: string
    is_overdue: boolean
    days_overdue: number | null
    status_reason: string | null
  }>
  unassigned: Array<{
    id: string
    control_number: string
    name: string
    category: string
    status: string
  }>
  missing_policies: Array<{
    id: string
    control_number: string
    name: string
    category: string
    status: string
    evidence_type: string
  }>
}

interface FrameworkListItem {
  id: string
  name: string
  display_name: string
  version: string | null
  description: string | null
  total_controls: number
  compliance_score: number
  overdue_controls: number
}

interface ComparisonData {
  frameworks: Array<{
    framework: {
      id: string
      name: string
      display_name: string
      version: string | null
    }
    metrics: {
      compliance_score: number
      total_controls: number
      applicable_controls: number
      compliant_controls: number
      non_compliant_controls: number
      in_progress_controls: number
      pending_controls: number
      overdue_controls: number
    }
    evidence: {
      total: number
      coverage_percentage: number
      last_30_days: number
    }
    documents: {
      total_policies: number
      approved_policies: number
      coverage_percentage: number
    }
    risk_exposure: {
      linked_risks: number
      open_risks: number
      high_critical_risks: number
    }
  }>
  insights: {
    average_compliance: number
    average_evidence_coverage: number
    best_performing: {
      framework_id: string
      framework_name: string
      score: number
    }
    worst_performing: {
      framework_id: string
      framework_name: string
      score: number
    }
    total_overdue_controls: number
    total_open_risks: number
  }
}

interface ActivityItem {
  type: 'evidence_uploaded' | 'control_updated'
  id: string
  title: string
  control_number?: string
  control_name?: string
  username?: string
  status?: string
  timestamp: number
}

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

function StatCard({
  title,
  value,
  icon: Icon,
  color,
  subtext,
}: {
  title: string
  value: string | number
  icon: React.ElementType
  color: string
  subtext?: string
}) {
  const colorClasses: Record<string, string> = {
    green: 'bg-green-50 text-green-600 border-green-200',
    red: 'bg-red-50 text-red-600 border-red-200',
    yellow: 'bg-yellow-50 text-yellow-600 border-yellow-200',
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    purple: 'bg-purple-50 text-purple-600 border-purple-200',
    gray: 'bg-gray-50 text-gray-600 border-gray-200',
  }

  return (
    <div className={`rounded-lg border p-4 ${colorClasses[color]}`}>
      <div className="flex items-center gap-3">
        <Icon className="h-8 w-8" />
        <div>
          <p className="text-sm font-medium opacity-80">{title}</p>
          <p className="text-2xl font-bold">{value}</p>
          {subtext && <p className="text-xs opacity-70">{subtext}</p>}
        </div>
      </div>
    </div>
  )
}

function ComplianceScoreRing({ score }: { score: number }) {
  const radius = 80
  const circumference = 2 * Math.PI * radius
  const strokeDashoffset = circumference - (score / 100) * circumference
  const color = score >= 80 ? '#22c55e' : score >= 60 ? '#f59e0b' : '#ef4444'

  return (
    <div className="relative flex items-center justify-center">
      <svg className="transform -rotate-90" width="200" height="200">
        <circle
          cx="100"
          cy="100"
          r={radius}
          stroke="#e5e7eb"
          strokeWidth="16"
          fill="none"
        />
        <circle
          cx="100"
          cy="100"
          r={radius}
          stroke={color}
          strokeWidth="16"
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute text-center">
        <p className="text-4xl font-bold" style={{ color }}>
          {score}%
        </p>
        <p className="text-sm text-gray-500">Compliance</p>
      </div>
    </div>
  )
}

function ExpandableSection({
  title,
  count,
  icon: Icon,
  color,
  children,
}: {
  title: string
  count: number
  icon: React.ElementType
  color: string
  children: React.ReactNode
}) {
  const [expanded, setExpanded] = useState(false)

  const colorClasses: Record<string, string> = {
    red: 'border-red-200 bg-red-50',
    yellow: 'border-yellow-200 bg-yellow-50',
    blue: 'border-blue-200 bg-blue-50',
    gray: 'border-gray-200 bg-gray-50',
    purple: 'border-purple-200 bg-purple-50',
  }

  return (
    <div className={`border rounded-lg ${colorClasses[color]} overflow-hidden`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-opacity-70 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Icon className="h-5 w-5" />
          <span className="font-medium">{title}</span>
          <span className="bg-white px-2 py-0.5 rounded-full text-sm font-medium">
            {count}
          </span>
        </div>
        {expanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
      </button>
      {expanded && <div className="border-t bg-white p-4">{children}</div>}
    </div>
  )
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function FrameworkDashboard() {
  const { frameworkId } = useParams<{ frameworkId: string }>()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [dashboard, setDashboard] = useState<FrameworkDashboardData | null>(null)
  const [trends, setTrends] = useState<TrendsData | null>(null)
  const [gaps, setGaps] = useState<GapsData | null>(null)
  const [trendDays, setTrendDays] = useState(90)
  const [activeTab, setActiveTab] = useState<'overview' | 'gaps' | 'trends' | 'compare' | 'activity'>('overview')

  // Framework selector state
  const [frameworkList, setFrameworkList] = useState<FrameworkListItem[]>([])
  const [showFrameworkSelector, setShowFrameworkSelector] = useState(false)

  // Comparison state
  const [selectedForComparison, setSelectedForComparison] = useState<string[]>([])
  const [comparison, setComparison] = useState<ComparisonData | null>(null)
  const [comparisonLoading, setComparisonLoading] = useState(false)

  // Activity state
  const [activity, setActivity] = useState<ActivityItem[]>([])
  const [activityLoading, setActivityLoading] = useState(false)

  // Fetch framework list for selector
  useEffect(() => {
    async function fetchFrameworkList() {
      try {
        const res = await api.getFrameworkList()
        if (res.success) {
          setFrameworkList(res.frameworks)
        }
      } catch (err) {
        console.error('Failed to fetch framework list:', err)
      }
    }
    fetchFrameworkList()
  }, [])

  useEffect(() => {
    if (!frameworkId) return

    const fwId = frameworkId // Capture for closure

    async function fetchData() {
      setLoading(true)
      setError(null)
      try {
        const [dashRes, trendsRes, gapsRes] = await Promise.all([
          api.getFrameworkDashboard(fwId),
          api.getFrameworkTrends(fwId, trendDays),
          api.getFrameworkGaps(fwId),
        ])

        if (dashRes.success) {
          setDashboard(dashRes.dashboard)
        } else {
          setError('Failed to load framework dashboard')
        }

        if (trendsRes.success) {
          setTrends(trendsRes.trends)
        }

        if (gapsRes.success) {
          setGaps(gapsRes.gaps)
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load data')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [frameworkId, trendDays])

  // Fetch activity when tab changes to activity
  useEffect(() => {
    if (activeTab !== 'activity' || !frameworkId) return

    const fwId = frameworkId // Capture for closure

    async function fetchActivity() {
      setActivityLoading(true)
      try {
        const res = await api.getFrameworkActivity(fwId, 20)
        if (res.success) {
          setActivity(res.activity)
        }
      } catch (err) {
        console.error('Failed to fetch activity:', err)
      } finally {
        setActivityLoading(false)
      }
    }
    fetchActivity()
  }, [activeTab, frameworkId])

  // Handle framework comparison
  const handleCompareFrameworks = async () => {
    if (selectedForComparison.length < 2) return

    setComparisonLoading(true)
    try {
      const res = await api.getFrameworkComparison(selectedForComparison)
      if (res.success) {
        setComparison(res.comparison)
      }
    } catch (err) {
      console.error('Failed to compare frameworks:', err)
    } finally {
      setComparisonLoading(false)
    }
  }

  // Toggle framework selection for comparison
  const toggleComparisonSelection = (id: string) => {
    setSelectedForComparison((prev) => {
      if (prev.includes(id)) {
        return prev.filter((fid) => fid !== id)
      }
      if (prev.length >= 5) return prev // Max 5 frameworks
      return [...prev, id]
    })
    setComparison(null) // Clear previous comparison when selection changes
  }

  // Handle framework switch from selector
  const handleFrameworkSwitch = (newFrameworkId: string) => {
    setShowFrameworkSelector(false)
    navigate(`/frameworks/${newFrameworkId}/dashboard`)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  if (error || !dashboard) {
    return (
      <div className="text-center py-12">
        <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500">{error || 'Framework not found'}</p>
        <button
          onClick={() => navigate('/frameworks')}
          className="mt-4 text-primary-600 hover:underline"
        >
          Back to Frameworks
        </button>
      </div>
    )
  }

  // Prepare chart data
  const statusPieData = [
    { name: 'Compliant', value: dashboard.controls.compliant, color: '#22c55e' },
    { name: 'In Progress', value: dashboard.controls.in_progress, color: '#3b82f6' },
    { name: 'Non-Compliant', value: dashboard.controls.non_compliant, color: '#ef4444' },
    { name: 'Pending', value: dashboard.controls.pending, color: '#9ca3af' },
  ].filter((d) => d.value > 0)

  const categoryBarData = dashboard.categories.map((cat) => ({
    name: cat.name.length > 20 ? cat.name.substring(0, 20) + '...' : cat.name,
    fullName: cat.name,
    compliant: cat.compliant,
    in_progress: cat.in_progress,
    non_compliant: cat.non_compliant,
    pending: cat.pending,
    compliance: cat.compliance_percentage,
  }))

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/frameworks')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="relative">
            <button
              onClick={() => setShowFrameworkSelector(!showFrameworkSelector)}
              className="flex items-center gap-2 hover:bg-gray-50 rounded-lg p-2 -m-2 transition-colors"
            >
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold text-gray-900">
                    {dashboard.framework.display_name}
                  </h1>
                  {dashboard.framework.version && (
                    <span className="px-2 py-0.5 text-sm bg-gray-100 rounded">
                      v{dashboard.framework.version}
                    </span>
                  )}
                  <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${showFrameworkSelector ? 'rotate-180' : ''}`} />
                </div>
              </div>
            </button>

            {/* Framework Selector Dropdown */}
            {showFrameworkSelector && (
              <div className="absolute top-full left-0 mt-2 w-80 bg-white rounded-lg shadow-lg border z-50 max-h-96 overflow-y-auto">
                <div className="p-3 border-b">
                  <p className="text-sm font-medium text-gray-500">Switch Framework</p>
                </div>
                {frameworkList.map((fw) => (
                  <button
                    key={fw.id}
                    onClick={() => handleFrameworkSwitch(fw.id)}
                    className={`w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center justify-between transition-colors ${
                      fw.id === frameworkId ? 'bg-primary-50' : ''
                    }`}
                  >
                    <div>
                      <p className="font-medium text-gray-900">{fw.display_name}</p>
                      <p className="text-sm text-gray-500">
                        {fw.total_controls} controls
                        {fw.overdue_controls > 0 && (
                          <span className="text-red-500 ml-2">
                            ({fw.overdue_controls} overdue)
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-sm font-medium ${
                          fw.compliance_score >= 80
                            ? 'text-green-600'
                            : fw.compliance_score >= 60
                            ? 'text-yellow-600'
                            : 'text-red-600'
                        }`}
                      >
                        {fw.compliance_score}%
                      </span>
                      {fw.id === frameworkId && (
                        <CheckCircle className="h-4 w-4 text-primary-600" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
            <p className="text-gray-500 mt-1">
              {dashboard.framework.description || 'Compliance Framework Dashboard'}
            </p>
          </div>
        </div>
        <Link
          to={`/controls?framework_id=${frameworkId}`}
          className="btn btn-primary"
        >
          View Controls
        </Link>
      </div>

      {/* Tabs */}
      <div className="border-b">
        <div className="flex gap-6">
          {(['overview', 'gaps', 'trends', 'activity', 'compare'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-3 px-1 font-medium transition-colors flex items-center gap-2 ${
                activeTab === tab
                  ? 'border-b-2 border-primary-600 text-primary-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab === 'overview' && 'Overview'}
              {tab === 'gaps' && 'Gap Analysis'}
              {tab === 'trends' && 'Trends'}
              {tab === 'activity' && (
                <>
                  <Activity className="h-4 w-4" />
                  Activity
                </>
              )}
              {tab === 'compare' && (
                <>
                  <GitCompare className="h-4 w-4" />
                  Compare
                </>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Top Stats Row */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Compliance Score Ring */}
            <div className="card p-6 flex flex-col items-center justify-center">
              <ComplianceScoreRing score={dashboard.compliance_score} />
            </div>

            {/* Control Stats */}
            <div className="lg:col-span-3 grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard
                title="Total Controls"
                value={dashboard.controls.total}
                icon={Layers}
                color="blue"
                subtext={`${dashboard.controls.applicable} applicable`}
              />
              <StatCard
                title="Compliant"
                value={dashboard.controls.compliant}
                icon={CheckCircle}
                color="green"
              />
              <StatCard
                title="In Progress"
                value={dashboard.controls.in_progress}
                icon={Clock}
                color="yellow"
              />
              <StatCard
                title="Non-Compliant"
                value={dashboard.controls.non_compliant}
                icon={XCircle}
                color="red"
              />
              <StatCard
                title="Pending"
                value={dashboard.controls.pending}
                icon={Target}
                color="gray"
              />
              <StatCard
                title="Overdue"
                value={dashboard.controls.overdue}
                icon={AlertTriangle}
                color="red"
                subtext="Need attention"
              />
              <StatCard
                title="Evidence Items"
                value={dashboard.evidence.total}
                icon={FileText}
                color="purple"
                subtext={`${dashboard.evidence.coverage_percentage}% coverage`}
              />
              <StatCard
                title="Linked Policies"
                value={dashboard.documents.total_policies}
                icon={FileCheck}
                color="blue"
                subtext={`${dashboard.documents.approved_policies} approved`}
              />
            </div>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Status Distribution Pie Chart */}
            <div className="card p-6">
              <h3 className="text-lg font-semibold mb-4">Control Status Distribution</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusPieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {statusPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Category Breakdown */}
            <div className="card p-6">
              <h3 className="text-lg font-semibold mb-4">Compliance by Category</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={categoryBarData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" domain={[0, 100]} unit="%" />
                    <YAxis dataKey="name" type="category" width={120} fontSize={12} />
                    <Tooltip
                      formatter={(value: number) => `${value}%`}
                      labelFormatter={(label) => {
                        const item = categoryBarData.find((d) => d.name === label)
                        return item?.fullName || label
                      }}
                    />
                    <Bar dataKey="compliance" fill="#22c55e" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Overdue & Upcoming Due */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Overdue Controls */}
            <div className="card p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                Overdue Controls
              </h3>
              {dashboard.overdue_controls.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No overdue controls</p>
              ) : (
                <div className="space-y-2">
                  {dashboard.overdue_controls.map((control) => (
                    <Link
                      key={control.id}
                      to={`/controls/${control.id}`}
                      className="block p-3 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900">{control.control_number}</p>
                          <p className="text-sm text-gray-600 line-clamp-1">{control.name}</p>
                        </div>
                        <span className="text-red-600 font-medium text-sm">
                          {control.days_overdue} days overdue
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Upcoming Due */}
            <div className="card p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Calendar className="h-5 w-5 text-yellow-500" />
                Upcoming Due
              </h3>
              {dashboard.upcoming_due.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No upcoming deadlines</p>
              ) : (
                <div className="space-y-2">
                  {dashboard.upcoming_due.map((control) => (
                    <Link
                      key={control.id}
                      to={`/controls/${control.id}`}
                      className="block p-3 bg-yellow-50 rounded-lg hover:bg-yellow-100 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900">{control.control_number}</p>
                          <p className="text-sm text-gray-600 line-clamp-1">{control.name}</p>
                        </div>
                        <span className="text-yellow-600 font-medium text-sm">
                          {control.days_until_due} days left
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Gap Analysis Tab */}
      {activeTab === 'gaps' && gaps && (
        <div className="space-y-6">
          {/* Gap Summary */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold mb-4">Gap Summary</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-3xl font-bold text-gray-900">{gaps.summary.gap_count}</p>
                <p className="text-sm text-gray-500">Total Gaps</p>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <p className="text-3xl font-bold text-red-600">{gaps.summary.non_compliant_count}</p>
                <p className="text-sm text-gray-500">Non-Compliant</p>
              </div>
              <div className="text-center p-4 bg-yellow-50 rounded-lg">
                <p className="text-3xl font-bold text-yellow-600">{gaps.summary.in_progress_count}</p>
                <p className="text-sm text-gray-500">In Progress</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-3xl font-bold text-gray-600">{gaps.summary.not_started_count}</p>
                <p className="text-sm text-gray-500">Not Started</p>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <p className="text-3xl font-bold text-purple-600">{gaps.summary.unassigned_count}</p>
                <p className="text-sm text-gray-500">Unassigned</p>
              </div>
            </div>
          </div>

          {/* Gap Details */}
          <div className="space-y-4">
            {/* Non-Compliant Controls */}
            <ExpandableSection
              title="Non-Compliant Controls"
              count={gaps.non_compliant.length}
              icon={XCircle}
              color="red"
            >
              {gaps.non_compliant.length === 0 ? (
                <p className="text-gray-500">No non-compliant controls</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left border-b">
                        <th className="pb-2 font-medium">Control</th>
                        <th className="pb-2 font-medium">Category</th>
                        <th className="pb-2 font-medium">Owner</th>
                        <th className="pb-2 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gaps.non_compliant.map((control) => (
                        <tr key={control.id} className="border-b hover:bg-gray-50">
                          <td className="py-2">
                            <Link
                              to={`/controls/${control.id}`}
                              className="text-primary-600 hover:underline font-medium"
                            >
                              {control.control_number}
                            </Link>
                            <p className="text-gray-500 text-xs">{control.name}</p>
                          </td>
                          <td className="py-2">{control.category || '-'}</td>
                          <td className="py-2">{control.owner}</td>
                          <td className="py-2">
                            {control.is_overdue ? (
                              <span className="text-red-600">
                                {control.days_overdue} days overdue
                              </span>
                            ) : (
                              <span className="text-gray-500">Non-compliant</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </ExpandableSection>

            {/* Not Started Controls */}
            <ExpandableSection
              title="Not Started"
              count={gaps.not_started.length}
              icon={Target}
              color="gray"
            >
              {gaps.not_started.length === 0 ? (
                <p className="text-gray-500">All controls have been started</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left border-b">
                        <th className="pb-2 font-medium">Control</th>
                        <th className="pb-2 font-medium">Category</th>
                        <th className="pb-2 font-medium">Owner</th>
                        <th className="pb-2 font-medium">Frequency</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gaps.not_started.map((control) => (
                        <tr key={control.id} className="border-b hover:bg-gray-50">
                          <td className="py-2">
                            <Link
                              to={`/controls/${control.id}`}
                              className="text-primary-600 hover:underline font-medium"
                            >
                              {control.control_number}
                            </Link>
                            <p className="text-gray-500 text-xs">{control.name}</p>
                          </td>
                          <td className="py-2">{control.category || '-'}</td>
                          <td className="py-2">{control.owner}</td>
                          <td className="py-2 capitalize">{control.frequency}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </ExpandableSection>

            {/* In Progress Controls */}
            <ExpandableSection
              title="In Progress"
              count={gaps.in_progress.length}
              icon={Clock}
              color="yellow"
            >
              {gaps.in_progress.length === 0 ? (
                <p className="text-gray-500">No controls in progress</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left border-b">
                        <th className="pb-2 font-medium">Control</th>
                        <th className="pb-2 font-medium">Category</th>
                        <th className="pb-2 font-medium">Owner</th>
                        <th className="pb-2 font-medium">Due</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gaps.in_progress.map((control) => (
                        <tr key={control.id} className="border-b hover:bg-gray-50">
                          <td className="py-2">
                            <Link
                              to={`/controls/${control.id}`}
                              className="text-primary-600 hover:underline font-medium"
                            >
                              {control.control_number}
                            </Link>
                            <p className="text-gray-500 text-xs">{control.name}</p>
                          </td>
                          <td className="py-2">{control.category || '-'}</td>
                          <td className="py-2">{control.owner}</td>
                          <td className="py-2">
                            {control.days_until_due !== null
                              ? `${control.days_until_due} days`
                              : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </ExpandableSection>

            {/* Unassigned Controls */}
            <ExpandableSection
              title="Unassigned Controls"
              count={gaps.unassigned.length}
              icon={Users}
              color="purple"
            >
              {gaps.unassigned.length === 0 ? (
                <p className="text-gray-500">All controls have assigned owners</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left border-b">
                        <th className="pb-2 font-medium">Control</th>
                        <th className="pb-2 font-medium">Category</th>
                        <th className="pb-2 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gaps.unassigned.map((control) => (
                        <tr key={control.id} className="border-b hover:bg-gray-50">
                          <td className="py-2">
                            <Link
                              to={`/controls/${control.id}`}
                              className="text-primary-600 hover:underline font-medium"
                            >
                              {control.control_number}
                            </Link>
                            <p className="text-gray-500 text-xs">{control.name}</p>
                          </td>
                          <td className="py-2">{control.category || '-'}</td>
                          <td className="py-2 capitalize">{control.status.replace('_', ' ')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </ExpandableSection>

            {/* Missing Policies */}
            <ExpandableSection
              title="Missing Required Policies"
              count={gaps.missing_policies.length}
              icon={FileText}
              color="blue"
            >
              {gaps.missing_policies.length === 0 ? (
                <p className="text-gray-500">All policy requirements are satisfied</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left border-b">
                        <th className="pb-2 font-medium">Control</th>
                        <th className="pb-2 font-medium">Category</th>
                        <th className="pb-2 font-medium">Evidence Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gaps.missing_policies.map((control) => (
                        <tr key={control.id} className="border-b hover:bg-gray-50">
                          <td className="py-2">
                            <Link
                              to={`/controls/${control.id}`}
                              className="text-primary-600 hover:underline font-medium"
                            >
                              {control.control_number}
                            </Link>
                            <p className="text-gray-500 text-xs">{control.name}</p>
                          </td>
                          <td className="py-2">{control.category || '-'}</td>
                          <td className="py-2 capitalize">{control.evidence_type}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </ExpandableSection>
          </div>
        </div>
      )}

      {/* Trends Tab */}
      {activeTab === 'trends' && trends && (
        <div className="space-y-6">
          {/* Trend Period Selector */}
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-500">Show trends for:</span>
            <div className="flex bg-gray-100 rounded-lg p-1">
              {[30, 60, 90].map((days) => (
                <button
                  key={days}
                  onClick={() => setTrendDays(days)}
                  className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                    trendDays === days
                      ? 'bg-white shadow text-primary-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {days} days
                </button>
              ))}
            </div>
          </div>

          {/* Compliance Trend Chart */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-500" />
              Compliance Trend
            </h3>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trends.compliance_timeline}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => {
                      const date = new Date(value)
                      return `${date.getMonth() + 1}/${date.getDate()}`
                    }}
                  />
                  <YAxis domain={[0, 100]} unit="%" tick={{ fontSize: 12 }} />
                  <Tooltip
                    formatter={(value: number) => [`${value}%`, 'Compliance']}
                    labelFormatter={(label) =>
                      new Date(label).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })
                    }
                  />
                  <Area
                    type="monotone"
                    dataKey="compliance_percentage"
                    stroke="#22c55e"
                    fill="#22c55e"
                    fillOpacity={0.2}
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Evidence Collection Trend */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-500" />
              Evidence Collection Activity
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trends.evidence_collection}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => {
                      const date = new Date(value)
                      return `${date.getMonth() + 1}/${date.getDate()}`
                    }}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip
                    formatter={(value: number) => [value, 'Evidence Items']}
                    labelFormatter={(label) =>
                      new Date(label).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })
                    }
                  />
                  <Bar dataKey="evidence_count" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Current Stats Summary */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold mb-4">Current Statistics</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-600">
                  {trends.current_stats.total_controls}
                </p>
                <p className="text-sm text-gray-500">Total Controls</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-600">
                  {trends.current_stats.applicable_controls}
                </p>
                <p className="text-sm text-gray-500">Applicable</p>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-600">
                  {trends.current_stats.compliant_controls}
                </p>
                <p className="text-sm text-gray-500">Compliant</p>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-600">
                  {trends.current_stats.compliance_percentage}%
                </p>
                <p className="text-sm text-gray-500">Compliance Rate</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Activity Tab */}
      {activeTab === 'activity' && (
        <div className="space-y-6">
          <div className="card p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Activity className="h-5 w-5 text-blue-500" />
              Recent Activity
            </h3>
            {activityLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
              </div>
            ) : activity.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No recent activity for this framework</p>
            ) : (
              <div className="space-y-3">
                {activity.map((item, idx) => (
                  <div
                    key={`${item.type}-${item.id}-${idx}`}
                    className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div
                      className={`p-2 rounded-lg ${
                        item.type === 'evidence_uploaded'
                          ? 'bg-green-100 text-green-600'
                          : 'bg-blue-100 text-blue-600'
                      }`}
                    >
                      {item.type === 'evidence_uploaded' ? (
                        <FileText className="h-5 w-5" />
                      ) : (
                        <CheckCircle className="h-5 w-5" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">
                          {item.type === 'evidence_uploaded' ? 'Evidence Uploaded' : 'Control Updated'}
                        </span>
                        {item.control_number && (
                          <Link
                            to={`/controls/${item.id}`}
                            className="text-primary-600 hover:underline text-sm"
                          >
                            {item.control_number}
                          </Link>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 truncate">{item.title || item.control_name}</p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                        {item.username && <span>by {item.username}</span>}
                        {item.status && (
                          <span className={`px-2 py-0.5 rounded-full ${
                            item.status === 'compliant' ? 'bg-green-100 text-green-700' :
                            item.status === 'non_compliant' ? 'bg-red-100 text-red-700' :
                            item.status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {item.status.replace('_', ' ')}
                          </span>
                        )}
                        <span>
                          {new Date(item.timestamp).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      </div>
                    </div>
                    <Link
                      to={`/controls/${item.id}`}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </Link>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Compare Tab */}
      {activeTab === 'compare' && (
        <div className="space-y-6">
          {/* Framework Selection */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <GitCompare className="h-5 w-5 text-purple-500" />
              Compare Frameworks
            </h3>
            <p className="text-gray-600 mb-4">
              Select 2-5 frameworks to compare their compliance metrics, evidence coverage, and risk exposure.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-4">
              {frameworkList.map((fw) => (
                <button
                  key={fw.id}
                  onClick={() => toggleComparisonSelection(fw.id)}
                  className={`p-3 rounded-lg border text-left transition-all ${
                    selectedForComparison.includes(fw.id)
                      ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-200'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">{fw.display_name}</span>
                    {selectedForComparison.includes(fw.id) && (
                      <CheckCircle className="h-4 w-4 text-primary-600" />
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-gray-500">{fw.total_controls} controls</span>
                    <span
                      className={`text-xs font-medium ${
                        fw.compliance_score >= 80
                          ? 'text-green-600'
                          : fw.compliance_score >= 60
                          ? 'text-yellow-600'
                          : 'text-red-600'
                      }`}
                    >
                      {fw.compliance_score}%
                    </span>
                  </div>
                </button>
              ))}
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={handleCompareFrameworks}
                disabled={selectedForComparison.length < 2 || comparisonLoading}
                className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {comparisonLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Comparing...
                  </>
                ) : (
                  <>
                    <GitCompare className="h-4 w-4 mr-2" />
                    Compare {selectedForComparison.length} Frameworks
                  </>
                )}
              </button>
              {selectedForComparison.length > 0 && (
                <button
                  onClick={() => {
                    setSelectedForComparison([])
                    setComparison(null)
                  }}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Clear selection
                </button>
              )}
            </div>
          </div>

          {/* Comparison Results */}
          {comparison && (
            <div className="space-y-6">
              {/* Insights Summary */}
              <div className="card p-6">
                <h3 className="text-lg font-semibold mb-4">Comparison Insights</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">
                      {comparison.insights.average_compliance}%
                    </p>
                    <p className="text-sm text-gray-500">Avg Compliance</p>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <p className="text-2xl font-bold text-purple-600">
                      {comparison.insights.average_evidence_coverage}%
                    </p>
                    <p className="text-sm text-gray-500">Avg Evidence Coverage</p>
                  </div>
                  <div className="text-center p-4 bg-red-50 rounded-lg">
                    <p className="text-2xl font-bold text-red-600">
                      {comparison.insights.total_overdue_controls}
                    </p>
                    <p className="text-sm text-gray-500">Total Overdue</p>
                  </div>
                  <div className="text-center p-4 bg-yellow-50 rounded-lg">
                    <p className="text-2xl font-bold text-yellow-600">
                      {comparison.insights.total_open_risks}
                    </p>
                    <p className="text-sm text-gray-500">Open Risks</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="p-4 bg-green-50 rounded-lg">
                    <p className="text-sm text-gray-500">Best Performing</p>
                    <p className="font-semibold text-green-700">
                      {comparison.insights.best_performing.framework_name}
                    </p>
                    <p className="text-2xl font-bold text-green-600">
                      {comparison.insights.best_performing.score}%
                    </p>
                  </div>
                  <div className="p-4 bg-red-50 rounded-lg">
                    <p className="text-sm text-gray-500">Needs Attention</p>
                    <p className="font-semibold text-red-700">
                      {comparison.insights.worst_performing.framework_name}
                    </p>
                    <p className="text-2xl font-bold text-red-600">
                      {comparison.insights.worst_performing.score}%
                    </p>
                  </div>
                </div>
              </div>

              {/* Radar Chart Comparison */}
              <div className="card p-6">
                <h3 className="text-lg font-semibold mb-4">Metrics Comparison</h3>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart
                      data={[
                        {
                          metric: 'Compliance',
                          ...comparison.frameworks.reduce(
                            (acc, fw) => ({ ...acc, [fw.framework.display_name]: fw.metrics.compliance_score }),
                            {}
                          ),
                        },
                        {
                          metric: 'Evidence',
                          ...comparison.frameworks.reduce(
                            (acc, fw) => ({ ...acc, [fw.framework.display_name]: fw.evidence.coverage_percentage }),
                            {}
                          ),
                        },
                        {
                          metric: 'Policies',
                          ...comparison.frameworks.reduce(
                            (acc, fw) => ({ ...acc, [fw.framework.display_name]: fw.documents.coverage_percentage }),
                            {}
                          ),
                        },
                        {
                          metric: 'Completion',
                          ...comparison.frameworks.reduce(
                            (acc, fw) => ({
                              ...acc,
                              [fw.framework.display_name]: fw.metrics.applicable_controls > 0
                                ? Math.round(
                                    ((fw.metrics.compliant_controls + fw.metrics.in_progress_controls) /
                                      fw.metrics.applicable_controls) *
                                      100
                                  )
                                : 0,
                            }),
                            {}
                          ),
                        },
                      ]}
                    >
                      <PolarGrid />
                      <PolarAngleAxis dataKey="metric" />
                      <PolarRadiusAxis angle={30} domain={[0, 100]} />
                      {comparison.frameworks.map((fw, idx) => (
                        <Radar
                          key={fw.framework.id}
                          name={fw.framework.display_name}
                          dataKey={fw.framework.display_name}
                          stroke={['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6'][idx % 5]}
                          fill={['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6'][idx % 5]}
                          fillOpacity={0.1}
                        />
                      ))}
                      <Legend />
                      <Tooltip />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Detailed Comparison Table */}
              <div className="card p-6">
                <h3 className="text-lg font-semibold mb-4">Detailed Comparison</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 font-medium">Metric</th>
                        {comparison.frameworks.map((fw) => (
                          <th key={fw.framework.id} className="text-center py-3 px-4 font-medium">
                            {fw.framework.display_name}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4 font-medium">Compliance Score</td>
                        {comparison.frameworks.map((fw) => (
                          <td key={fw.framework.id} className="text-center py-3 px-4">
                            <span
                              className={`font-bold ${
                                fw.metrics.compliance_score >= 80
                                  ? 'text-green-600'
                                  : fw.metrics.compliance_score >= 60
                                  ? 'text-yellow-600'
                                  : 'text-red-600'
                              }`}
                            >
                              {fw.metrics.compliance_score}%
                            </span>
                          </td>
                        ))}
                      </tr>
                      <tr className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4 font-medium">Total Controls</td>
                        {comparison.frameworks.map((fw) => (
                          <td key={fw.framework.id} className="text-center py-3 px-4">
                            {fw.metrics.total_controls}
                          </td>
                        ))}
                      </tr>
                      <tr className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4 font-medium">Compliant</td>
                        {comparison.frameworks.map((fw) => (
                          <td key={fw.framework.id} className="text-center py-3 px-4 text-green-600">
                            {fw.metrics.compliant_controls}
                          </td>
                        ))}
                      </tr>
                      <tr className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4 font-medium">Non-Compliant</td>
                        {comparison.frameworks.map((fw) => (
                          <td key={fw.framework.id} className="text-center py-3 px-4 text-red-600">
                            {fw.metrics.non_compliant_controls}
                          </td>
                        ))}
                      </tr>
                      <tr className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4 font-medium">In Progress</td>
                        {comparison.frameworks.map((fw) => (
                          <td key={fw.framework.id} className="text-center py-3 px-4 text-yellow-600">
                            {fw.metrics.in_progress_controls}
                          </td>
                        ))}
                      </tr>
                      <tr className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4 font-medium">Overdue</td>
                        {comparison.frameworks.map((fw) => (
                          <td key={fw.framework.id} className="text-center py-3 px-4 text-red-600 font-medium">
                            {fw.metrics.overdue_controls}
                          </td>
                        ))}
                      </tr>
                      <tr className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4 font-medium">Evidence Coverage</td>
                        {comparison.frameworks.map((fw) => (
                          <td key={fw.framework.id} className="text-center py-3 px-4">
                            {fw.evidence.coverage_percentage}%
                          </td>
                        ))}
                      </tr>
                      <tr className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4 font-medium">Evidence (Last 30d)</td>
                        {comparison.frameworks.map((fw) => (
                          <td key={fw.framework.id} className="text-center py-3 px-4">
                            {fw.evidence.last_30_days}
                          </td>
                        ))}
                      </tr>
                      <tr className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4 font-medium">Total Policies</td>
                        {comparison.frameworks.map((fw) => (
                          <td key={fw.framework.id} className="text-center py-3 px-4">
                            {fw.documents.total_policies}
                          </td>
                        ))}
                      </tr>
                      <tr className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4 font-medium">Approved Policies</td>
                        {comparison.frameworks.map((fw) => (
                          <td key={fw.framework.id} className="text-center py-3 px-4 text-green-600">
                            {fw.documents.approved_policies}
                          </td>
                        ))}
                      </tr>
                      <tr className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4 font-medium">Linked Risks</td>
                        {comparison.frameworks.map((fw) => (
                          <td key={fw.framework.id} className="text-center py-3 px-4">
                            {fw.risk_exposure.linked_risks}
                          </td>
                        ))}
                      </tr>
                      <tr className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4 font-medium">Open Risks</td>
                        {comparison.frameworks.map((fw) => (
                          <td key={fw.framework.id} className="text-center py-3 px-4 text-yellow-600">
                            {fw.risk_exposure.open_risks}
                          </td>
                        ))}
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="py-3 px-4 font-medium">High/Critical Risks</td>
                        {comparison.frameworks.map((fw) => (
                          <td key={fw.framework.id} className="text-center py-3 px-4 text-red-600 font-medium">
                            {fw.risk_exposure.high_critical_risks}
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
